import { Button, Card, Col, Collapse, notification, Row, Space, Typography } from 'antd';

import { GenerateWaybillsButton, ViewWaybillsButton } from 'components/common/WaybillComponents';
import { DefectiveReturnItemPayload, DefectiveReturnsPayload, SparingRequest } from 'models/SparingRequests';
import { Package } from 'models/Waybill';

import { colors, toRgba } from 'styles/colors';

import { useEffect, useState } from 'react';
import { useUpdateSparingRequestMutation } from 'redux/services/twittlebugs/sparingRequestsApi';
import { useSendWaybillEmailMutation } from 'redux/services/waybillApi';
import { DefectiveReturnsTableReadOnly } from './DefectiveReturnsTableReadOnly';

type Props = {
  defectiveReturns?: DefectiveReturnsPayload;
  parentData?: SparingRequest; // Add parent data for waybill functionality
  sparingOrders?: Array<{
    id: string;
    status: string;
    partitionKey: string;
  }>; // Add sparing orders for waybill generation
};

export const DefectiveReturnsInformationCard = ({ defectiveReturns, parentData, sparingOrders }: Props): JSX.Element => {
  const items = defectiveReturns?.items ?? [];
  const [updatedItems, setUpdatedItems] = useState<DefectiveReturnItemPayload[]>(items);
  const [updateSparing, { isLoading: isSaving }] = useUpdateSparingRequestMutation(); // Add this hook

  // Sync updatedItems when defectiveReturns changes
  useEffect(() => {
    setUpdatedItems(items);
  }, [items]);

  const handleSave = async (): Promise<void> => {
    if (!parentData?.id) return;

    await updateSparing({
      id: parentData.id,
      body: {
        ...parentData,
        outageLocationId: parentData.outageLocation?.id ?? '',
        shippingLocationId: parentData.shippingLocation?.id ?? '',
        lineItems:
          parentData.lineItems?.map((item, index) => ({
            ...item,
            lineNumber: index + 1
          })) ?? [],
        defectiveReturns: {
          contactName: defectiveReturns?.contactName ?? '',
          contactPhoneNumber: defectiveReturns?.contactPhoneNumber ?? '',
          contactEmail: defectiveReturns?.contactEmail ?? '',
          waybillDeliveryMethods: defectiveReturns?.waybillDeliveryMethods,
          items: updatedItems
        }
      }
    }).unwrap();
  };

  // Prepare waybill data from defective return items
  // Each item can have its own waybill according to the C# model
  const itemsWithWaybills = defectiveReturns?.items?.filter((item) => item.waybill) || [];

  // Helper function to convert old waybill format to new format
  const convertToNewWaybillFormat = (oldWaybill: any, divisionId: string, sparingRequestId: string) => ({
    ...oldWaybill,
    divisionId,
    sparingRequestId
  });

  // Create packages data for waybill functionality
  // Group items by waybill or create individual packages
  const allWaybillData: Package[] = itemsWithWaybills.map((item, index) => ({
    key: index,
    numberOfPackages: 1,
    packageSize: 'Standard',
    packageWeight: 0,
    waybill: item.waybill ? [convertToNewWaybillFormat(item.waybill, parentData?.id || '', parentData?.id || '')] : []
  }));

  // Also check if there are direct waybills in defectiveReturns (fallback)
  const directWaybills = defectiveReturns?.waybills || [];

  if (directWaybills.length > 0 && allWaybillData.length === 0) {
    allWaybillData.push({
      key: 0,
      numberOfPackages: directWaybills.length,
      packageSize: 'Standard',
      packageWeight: 0,
      waybill: directWaybills.map((waybill) => convertToNewWaybillFormat(waybill, parentData?.id || '', parentData?.id || ''))
    });
  }

  const [sendWaybillEmail, { isLoading: isSendingEmail }] = useSendWaybillEmailMutation();

  // Handle waybills generated callback
  const handleWaybillsGenerated = (): void => {
    // After generating waybills, the data should now be available in defectiveReturns
    // No need to reload the page - the data will be updated via the API call
  };

  // Determine if we should show the generate waybills button
  const shouldShowGenerateWaybills = (defectiveReturns?.items?.length || 0) > 0;

  // Convert SparingRequest to SparingRequestPayload format for waybill generation
  const waybillData = parentData
    ? {
        contactEmail: defectiveReturns?.contactEmail || parentData?.contact?.email || '',
        contactName: defectiveReturns?.contactName || parentData?.contact?.name || '',
        contactPhoneNumber: defectiveReturns?.contactPhoneNumber || parentData?.contact?.phoneNumber || '',
        customerReferenceNumber: parentData?.customerReferenceNumber || '',
        customerPurchaseOrderNumber: parentData?.customerPurchaseOrderNumber || '',
        isLabConfigurationRequired: parentData?.isLabConfigurationRequired || false,
        lineItems: (parentData?.lineItems || []).map((item, index) => ({
          ...item,
          lineNumber: index + 1, // Generate line number based on array index
          productNumber: item.productNumber || '',
          quantity: item.quantity || 0,
          subLines: item.subLines || []
        })),
        notes: parentData?.notes || '',
        notificationEmails: parentData?.notificationEmails || [],
        outageLocationId: parentData?.outageLocation?.id || '',
        requestedDeliveryDate: parentData?.requestedDeliveryDate || '',
        requestNumber: parentData?.requestNumber || '',
        shippingLocationId: parentData?.shippingLocation?.id || '',
        sparingRequestTypeId: parentData?.sparingRequestTypeId || '',
        isUserOptedIn: parentData?.isUserOptedIn || false,

        defectiveReturns: defectiveReturns,
        // Add the original sparing request ID for waybill generation
        id: sparingOrders?.[0]?.partitionKey || '',
        status: sparingOrders?.[0]?.status || '',
        // Add the sparing request ID for updating defective returns
        sparingRequestId: parentData?.id || ''
      }
    : null;

  return (
    <Card
      className="narrow-card-head"
      headStyle={{ backgroundColor: toRgba(colors.illuminatingEmerald, 0.3) }}
      title={
        <Typography.Title level={4} style={{ fontWeight: 'normal', margin: 0, padding: 0 }}>
          Defective Returns
        </Typography.Title>
      }
      extra={
        <Space wrap>
          <Button type="primary" loading={isSaving} onClick={handleSave}>
            Save
          </Button>
          {waybillData && <GenerateWaybillsButton openData={waybillData} onWaybillsGenerated={handleWaybillsGenerated} sparingRequestId={waybillData.sparingRequestId} />}
          <ViewWaybillsButton packagesWithWaybill={allWaybillData} sparingRequestName={parentData?.requestNumber || 'Sparing Request'} />
          {waybillData && (
            <Button
              type="primary"
              loading={isSendingEmail} // RTK Query provides loading state automatically!
              onClick={async (): Promise<void> => {
                if (!parentData?.division?.id || !parentData?.id) return;

                try {
                  await sendWaybillEmail({
                    divisionId: parentData.division.id,
                    sparingRequestId: parentData.id
                  }).unwrap(); // .unwrap() throws on error, matching your current behavior

                  notification.success({
                    message: 'Success',
                    description: 'Waybill email sent successfully',
                    duration: 5
                  });
                } catch (error) {
                  // RTK Query automatically handles errors, but you can add custom handling
                  console.error('Failed to send waybill email:', error);
                  notification.error({
                    message: 'Error',
                    description: 'Failed to send waybill email',
                    duration: 5
                  });
                }
              }}>
              Resend Waybills
            </Button>
          )}
        </Space>
      }>
      <Collapse>
        <Collapse.Panel header={`Defective Returns (${items.length})`} key="defective-returns">
          <Row gutter={[0, 16]}>
            <Col span={24}>
              <Card
                size="small"
                title={
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 16px' }}>
                    <span style={{ fontWeight: 'bold' }}>Contact Information</span>
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
                      <strong>Name:</strong> {defectiveReturns?.contactName || '—'}
                    </span>
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
                      <strong>Phone:</strong> {defectiveReturns?.contactPhoneNumber || '—'}
                    </span>
                    <span style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
                      <strong>Email:</strong> {defectiveReturns?.contactEmail || '—'}
                    </span>
                  </div>
                }
                bodyStyle={{ padding: '12px' }}>
                {/* Contact info moved to header, body is now minimal */}
              </Card>
            </Col>

            <Col span={24}>
              <DefectiveReturnsTableReadOnly items={items} onItemChange={setUpdatedItems} />
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>
    </Card>
  );
};
